// 📄 clienteService.ts
// `clientes/{clienteId}` es una colección preexistente del backend de
// facturación/Ariba/SUNAT (ver functions/services/configService.js y
// facturaService.js): el ID DEL DOCUMENTO es el nombre del cliente
// (p. ej. clientes/Zafranal), sin ningún campo interno "name"/"nombre" —
// de ahí cuelgan subcolecciones como `credenciales` y `proveedores`.
// Este servicio respeta esa convención: nunca inventa un id autogenerado,
// el nombre que escribe el usuario ES el id del documento, y nunca se
// puede cambiar después (renombrarlo implicaría migrar sus subcolecciones).
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase';

export interface AppOption {
  value: string;
  label: string;
}

// Mismas opciones que usa el formulario de creación y el de edición —
// una sola fuente para que nunca queden desincronizados.
export const APP_OPTIONS: AppOption[] = ['Commerce', 'Sourcing', 'SLP', 'Contracts', 'Buying'].map((app) => ({ value: app, label: app }));

// CIG (Cloud Integration Gateway) no se muestra como checkbox: todo cliente
// nuevo lo lleva activado de forma transparente (ver createCliente).
export const DEFAULT_APP = 'CIG';

export interface ClienteData {
  name: string; // se usa como ID del documento, no se guarda como campo
  email: string;
  apps: string[];
  // Sub-fase D/G1: org dueña del cliente. Aditivo — nunca se renombra ni
  // se usa para cambiar el ID/shape del doc (ver comentario de archivo).
  orgId: string;
}

export interface Cliente {
  id: string;
  name: string; // = id, expuesto como campo para no romper a quien ya lo consume así
  email: string;
  apps: string[];
  activo: boolean;
  createdAt?: any;
  // Ausente en clientes creados antes de la Sub-fase D (backfill) — ver
  // scripts/backfill-orgid-projects-clientes.mjs.
  orgId?: string;
  // Campos informativos, sin uso por el backend de facturación/Ariba/SUNAT
  // (que solo lee email/apps/orgId de este doc) — ausentes en clientes
  // creados antes de este campo.
  sector?: string;
  pais?: string;
  descripcion?: string;
}

const readCliente = (id: string, data: any): Cliente => ({
  id,
  name: id,
  email: data.email || '',
  apps: data.apps || [],
  // Los clientes creados antes de este campo no lo tienen — se tratan
  // como activos, para no ocultar de golpe nada que ya existía.
  activo: data.activo !== false,
  createdAt: data.createdAt,
  orgId: data.orgId,
  sector: data.sector || '',
  pais: data.pais || '',
  descripcion: data.descripcion || ''
});

export const createCliente = async (data: ClienteData): Promise<string> => {
  const clienteId = data.name.trim();
  if (!clienteId) throw new Error("El nombre del cliente es obligatorio.");
  if (clienteId.includes('/')) throw new Error("El nombre del cliente no puede contener '/'.");
  if (!data.orgId) throw new Error("orgId es obligatorio.");

  const ref = doc(db, "clientes", clienteId);

  // Evita pisar un cliente real ya existente (p. ej. uno con credenciales
  // de facturación cargadas) si alguien registra un nombre que coincide.
  const existing = await getDoc(ref);
  if (existing.exists()) {
    throw new Error(`Ya existe un cliente registrado como "${clienteId}".`);
  }

  await setDoc(ref, {
    email: data.email || '',
    apps: Array.from(new Set([...(data.apps || []), DEFAULT_APP])),
    activo: true,
    orgId: data.orgId,
    createdAt: serverTimestamp()
  });
  return clienteId;
};

/**
 * TODOS los clientes de la plataforma, sin filtrar por org — Sub-fase G2
 * restringió `clientes.read` a miembros/owner de la org dueña de cada
 * cliente, así que esta query sin filtro solo funciona para un admin (la
 * única rama que las rules siguen dejando pasar sin restricción de
 * `orgId`). No usar para nadie más — usar listClientesForOrg().
 */
export const listClientes = async (): Promise<Cliente[]> => {
  const snap = await getDocs(collection(db, "clientes"));
  return snap.docs.map(d => readCliente(d.id, d.data()));
};

/**
 * Clientes de una organización puntual (Sub-fase G2) — la query que debe
 * usar cualquier no-admin, ya que coincide con lo que firestore.rules
 * puede autorizar sin depender de list() sin filtro (ver
 * proyecto_refactor_organizaciones, hallazgo de Fase 0 de G2: un list()
 * sin where() que matchee la regla se rechaza ENTERO, no se filtra en
 * silencio doc por doc).
 */
export const listClientesForOrg = async (orgId: string): Promise<Cliente[]> => {
  const q = query(collection(db, "clientes"), where("orgId", "==", orgId));
  const snap = await getDocs(q);
  return snap.docs.map(d => readCliente(d.id, d.data()));
};

export const getCliente = async (clienteId: string): Promise<Cliente | null> => {
  if (!clienteId) return null;
  const snap = await getDoc(doc(db, "clientes", clienteId));
  if (!snap.exists()) return null;
  return readCliente(snap.id, snap.data());
};

/**
 * Actualiza email/apps/datos informativos de un cliente. El nombre (ID del
 * documento) nunca se toca acá — cambiarlo implicaría migrar
 * credenciales/proveedores.
 */
export const updateCliente = async (clienteId: string, data: { email: string; apps: string[]; sector?: string; pais?: string; descripcion?: string }) => {
  await updateDoc(doc(db, "clientes", clienteId), {
    email: data.email || '',
    apps: data.apps || [],
    sector: data.sector || '',
    pais: data.pais || '',
    descripcion: data.descripcion || ''
  });
};

export const setClienteActivo = async (clienteId: string, activo: boolean) => {
  await updateDoc(doc(db, "clientes", clienteId), { activo });
};
